import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Check, Copy, Download, Loader2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { BankTransferInfo } from '@/hooks/use-billing'
import { Button } from '@/components/ui/button'
import { formatVnd } from '@/lib/format-money'
import { i18n } from '@/lib/i18n'

/** Thời gian còn lại "mm:ss" (≥ 1 giờ → "h:mm:ss"); hết → null. */
export function remainingText(expiresAt: string | null, now: number): string | null {
  if (!expiresAt) return null
  const ms = new Date(expiresAt).getTime() - now
  if (!Number.isFinite(ms) || ms <= 0) return null
  const total = Math.floor(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
}

/** Tên tệp ảnh QR: "QR-<nội dung chuyển khoản>.png" (bỏ ký tự lạ). */
export function qrFileName(content: string): string {
  const safe = content.replace(/[^A-Za-z0-9._-]+/g, '').slice(0, 40)
  return `QR-${safe || i18n.t('billing:bankTransfer.qrFileFallback')}.png`
}

/** Tải ảnh QR về máy (điện thoại: lưu vào thư viện để mở app ngân hàng quét). Ảnh khác nguồn không tải thẳng được → mở tab mới để nhấn giữ lưu. */
async function saveQrImage(url: string, content: string) {
  try {
    const blob = await (await fetch(url)).blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = qrFileName(content)
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000)
  } catch {
    window.open(url, '_blank', 'noopener')
    toast.info(i18n.t('billing:bankTransfer.holdToSave'))
  }
}

function CopyRow({ label, value, display, strong }: { label: string; value: string; display?: string; strong?: boolean }) {
  const { t } = useTranslation('billing')
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error(t('bankTransfer.copyFailed'))
    }
  }
  return (
    <div className="flex items-center justify-between gap-3 border-b py-2 last:border-b-0">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="flex min-w-0 items-center gap-1.5">
        <span className={strong ? 'truncate font-mono font-semibold text-primary' : 'truncate font-medium'}>{display ?? value}</span>
        <button type="button" onClick={copy} className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={t('bankTransfer.copyAriaLabel', { label: label.toLowerCase() })}>
          {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </span>
    </div>
  )
}

/**
 * Khung chuyển khoản VietQR (EvoPay → SePay): ảnh QR + thông tin chuyển khoản + đếm ngược.
 * Hệ thống tự xác nhận khi tiền về đúng nội dung; trang cha tự hỏi trạng thái đơn (3 giây/lần).
 */
export function BankTransferPanel({ info, expiresAt, orderCode, onClose, onRenew, renewing, onRecheck, rechecking }: {
  info: BankTransferInfo
  expiresAt: string | null
  /** Mã đơn (EvoPay) — khách đọc cho bộ phận hỗ trợ khi tiền đã chuyển mà chưa kích hoạt. */
  orderCode?: string | null
  onClose: () => void
  /** Mã hết hạn → nút "Tạo mã mới". */
  onRenew?: () => void
  renewing?: boolean
  /** "Kiểm tra lại ngay" — hỏi lại trạng thái đơn. */
  onRecheck?: () => void
  rechecking?: boolean
}) {
  const { t } = useTranslation('billing')
  const [now, setNow] = useState(() => Date.now())
  const [qrBroken, setQrBroken] = useState(false)
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  const left = remainingText(expiresAt, now)

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="font-medium">{t('bankTransfer.title')}</p>
          <p className="text-muted-foreground">
            {t('bankTransfer.instructionPrefix')} <b>{t('bankTransfer.instructionBold')}</b> {t('bankTransfer.instructionSuffix')}
          </p>
        </div>
        <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label={t('common:actions.close')}>
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="mx-auto flex shrink-0 flex-col items-center gap-2 sm:mx-0">
        {qrBroken ? (
          <div className="flex h-56 w-56 shrink-0 items-center justify-center rounded-md border bg-white p-3 text-center text-xs text-muted-foreground">
            {t('bankTransfer.qrLoadFailed')}
          </div>
        ) : (
          <img
            src={info.qrImageUrl}
            alt={t('bankTransfer.qrAlt', { amount: formatVnd(info.amount), content: info.content })}
            onError={() => setQrBroken(true)}
            className="h-56 w-56 shrink-0 rounded-md border bg-white object-contain p-2"
          />
        )}
        <Button type="button" size="sm" variant="outline" className="w-full" onClick={() => void saveQrImage(info.qrImageUrl, info.content)}>
          <Download /> {t('bankTransfer.downloadQr')}
        </Button>
        </div>
        <div className="min-w-0 flex-1">
          <CopyRow label={t('bankTransfer.bank')} value={info.bankCode} />
          <CopyRow label={t('bankTransfer.accountNumber')} value={info.accountNumber} />
          {info.accountName && <CopyRow label={t('bankTransfer.accountName')} value={info.accountName} />}
          <CopyRow label={t('bankTransfer.amount')} value={String(info.amount)} display={formatVnd(info.amount)} />
          <CopyRow label={t('bankTransfer.content')} value={info.content} strong />
          {left ? (
            <>
              <p className="mt-3 flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                {t('bankTransfer.waitingPrefix')} <span className="font-mono tabular-nums">{left}</span>
              </p>
              {expiresAt && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('bankTransfer.keptUntil', { time: new Date(expiresAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }) })}
                </p>
              )}
            </>
          ) : (
            <div className="mt-3 space-y-2">
              <p className="text-muted-foreground">{t('bankTransfer.codeExpired')}</p>
              {onRenew && (
                <Button size="sm" onClick={onRenew} disabled={renewing}>
                  {renewing && <Loader2 className="animate-spin" />} {t('orderActions.newCode')}
                </Button>
              )}
            </div>
          )}
          <details className="mt-3 rounded-md border bg-background/60 px-3 py-2 text-xs text-muted-foreground">
            <summary className="cursor-pointer select-none text-sm font-medium text-foreground">{t('bankTransfer.notActivatedYet')}</summary>
            <div className="mt-2 space-y-2">
              <ul className="list-disc space-y-1 pl-4">
                <li>{t('bankTransfer.checklistAmount')} <b>{formatVnd(info.amount)}</b> {t('bankTransfer.checklistContent')} <b className="font-mono">{info.content}</b>{t('bankTransfer.checklistContentSuffix')}</li>
                <li>{t('bankTransfer.checklistTiming')}</li>
              </ul>
              {onRecheck && (
                <Button size="sm" variant="outline" onClick={onRecheck} disabled={rechecking}>
                  {rechecking && <Loader2 className="animate-spin" />} {t('bankTransfer.recheckNow')}
                </Button>
              )}
              {orderCode && <CopyRow label={t('bankTransfer.orderCodeForSupport')} value={orderCode} />}
              <p>{t('bankTransfer.stillNotActivated')}</p>
            </div>
          </details>
        </div>
      </div>
    </div>
  )
}
